import { z } from 'zod';
/**
 * `/widget` namespace WebSocket 이벤트 페이로드 (Wave 0 골격).
 *
 * Socket.IO namespace 역할 (ARCHITECTURE §3):
 *   io.of('/widget') — 스트리머 room 단위, OBS 브라우저 소스 소비자.
 *
 * 현 plan 은 toast 1 이벤트만. 후속 plan 에서 GoalReached, MissionCompleted,
 * TierUpgraded, StateMachineReset 등 추가.
 */
/**
 * DonationToast — 위젯 토스트·TTS 트리거 이벤트.
 *
 * 배포 방향: apps/realtime → OBS widget client
 * 트리거: `DonationCompletedEvent` (outbox) → realtime consumer → 본 페이로드로 emit.
 */
export const widgetDonationToastSchema = z.object({
    type: z.literal('donation.toast'),
    donationId: z.string().uuid(),
    /** KRW 정수 (tier 계산 + 표기) */
    amountKrw: z.number().int().nonnegative(),
    nickname: z.string().min(1).max(32),
    /** theme.json `tiers.labels` 와 일치 */
    tier: z.enum(['small', 'medium', 'large']),
    message: z.string().max(200).nullable(),
});
/**
 * TtsReady — CLOVA Voice 합성 완료 이벤트 (Plan 05-03 / WIDGET-03).
 *
 * 배포 방향: apps/api TtsWorker → XADD `loop:io:broadcast` → apps/realtime `/widget`
 *             → OBS widget client.
 *
 * 위젯은 `audioUrl` 을 `<audio>` 요소로 재생 (HTML5 audio). audioUrl 은
 * apps/api 의 `GET /api/v1/tts/:ttsId` 로 5분 TTL 캐시된 binary 를 스트리밍.
 *
 * T-05-17 Spoofing 완화: 위젯은 audioUrl 의 prefix 가 TTS_PUBLIC_URL 과 일치하는지
 * 검증 후에만 재생 (Plan 05-05 구현).
 */
export const widgetTtsReadySchema = z.object({
    type: z.literal('donation.tts_ready.v1'),
    /** 연결된 후원 id — donation.completed.v1 과 매핑. */
    donationId: z.string().uuid(),
    /** 본 TTS 결과의 고유 id (Redis key `tts:<ttsId>` 와 audioUrl path 양쪽에 사용). */
    ttsId: z.string().uuid(),
    /** 위젯이 재생할 audio 소스. `${TTS_PUBLIC_URL}/api/v1/tts/<ttsId>` 포맷. */
    audioUrl: z.string().url(),
    /** 합성 원본 text — 위젯이 자막 표시에도 사용. */
    text: z.string().min(1).max(200),
    /** 근사 재생 길이 (ms). 실측은 위젯이 audio 로드 시 확정. */
    durationMs: z.number().int().nonnegative(),
});
/**
 * TtsFailed — 합성 실패 (텍스트 초과 · CLOVA 3회 실패 · 타임아웃) 이벤트.
 *
 * 위젯은 본 이벤트 수신 시 오디오 없이 toast 만 표시하여 후원 누락을 방지.
 */
export const widgetTtsFailedSchema = z.object({
    type: z.literal('donation.tts_failed.v1'),
    donationId: z.string().uuid(),
    reason: z.enum([
        'text_too_long',
        'empty_text',
        'blocked_by_filter',
        'synthesis_failed',
        'timeout',
    ]),
});
/**
 * FilterTriggered — 욕설·스팸 필터 위반 이벤트 (Plan 05-04 / WIDGET-04).
 *
 * 배포 방향: apps/api TtsService (filter gate) → XADD `loop:io:broadcast` → apps/realtime
 *           `/widget` → OBS widget client.
 *
 * **시청자 뷰 숨김 규칙** (DESIGN.md §3.1 #9 "시청자 뷰 노출 X"):
 *   - 본 이벤트는 스트리머 프리뷰 전용 — apps/widget 의 viewer mode 에서는 rendering skip.
 *   - payload 는 전송되지만 UI 계층에서 defense in depth 로 차단 (T-05-25 EoP 완화).
 *
 * reason → layer 매핑:
 *   - 'profanity'          : global_dictionary 매칭 (text 필드)
 *   - 'streamer_blocklist' : streamer_blocklist 매칭 (text 필드)
 *   - 'clean_bot'          : Naver Clean Bot 판정
 *   - 'nickname_blocked'   : 닉네임 필드 매칭 (layer 무관)
 */
export const widgetFilterTriggeredSchema = z.object({
    type: z.literal('donation.filter_triggered.v1'),
    donationId: z.string().uuid(),
    streamerId: z.string().uuid(),
    reason: z.enum(['profanity', 'streamer_blocklist', 'clean_bot', 'nickname_blocked']),
    /** 매칭 룰 (global_dictionary/streamer_blocklist 는 해당 단어, clean_bot 은 category). */
    matchedRule: z.string().nullable(),
    /** 어느 layer 가 block 을 발동시켰는지. */
    layer: z.enum(['global_dictionary', 'streamer_blocklist', 'clean_bot']),
    /** 스트리머 프리뷰 전용 — 원 닉네임 노출 (시청자에는 숨김). */
    originalNickname: z.string(),
    /** 정규화된 text 의 앞부분 (max 64자) — 스트리머 프리뷰 디버깅. */
    normalizedTextPreview: z.string().max(64),
});
/**
 * GoalReached — 후원 목표 달성 이벤트 (Plan 05-02 발행 · 05-03 에서 계약 문서화).
 *
 * WidgetGoalService.incrementByDonation → markReachedIfNotYet RETURNING 기반 최초
 * 1회만 발행. 위젯은 본 이벤트 수신 시 DESIGN.md §3.1 #11 "goal-reached" 상태로
 * 전이 (중앙 bounce 애니메이션).
 */
export const widgetGoalReachedSchema = z.object({
    type: z.literal('goal.reached.v1'),
    goalId: z.string().uuid(),
    streamerId: z.string().uuid(),
    title: z.string().min(1),
    targetKrw: z.number().int().nonnegative(),
    currentKrw: z.number().int().nonnegative(),
    /** ISO 8601 with offset. */
    reachedAt: z.string().datetime({ offset: true }),
});
/**
 * AmaAnswer — 스트리머가 답변 선택한 AMA 질문 브로드캐스트 (Plan 03-03 / COMM-05).
 *
 * 배포 방향: apps/api AmaService.answerSelected → Outbox `community.ama_answer.v1` →
 *             OutboxPoller → Redis Stream `widget:{streamerId}` → apps/realtime /widget
 *             → OBS widget client (우측 하단 rail 순환).
 *
 * **익명성 계약 (T-03-03-04 Information Disclosure)**:
 *   - `askedByDisplay` 는 `is_anonymous=true` 인 경우 항상 `'익명'` 문자열 고정.
 *   - 실제 user.id / handle 은 DB 원본에만 존재 — 본 payload 에 포함 금지.
 */
export const widgetAmaAnswerSchema = z.object({
    type: z.literal('community.ama_answer.v1'),
    streamerId: z.string().uuid(),
    questionId: z.string().uuid(),
    /** 질문 제목 (posts.title). 최대 200자. */
    questionTitle: z.string().min(1).max(200),
    /** 질문 본문 일부 (posts.content 를 위젯용으로 축약). 최대 2000자. */
    questionBody: z.string().max(2000),
    /** 스트리머 답변 본문. 1~500자. */
    answerText: z.string().min(1).max(500),
    /** 팬 표시 이름 — 익명이면 '익명' 고정. 그 외엔 handle. 최대 32자. */
    askedByDisplay: z.string().min(1).max(32),
    /** 발행 시각 (ISO 8601 with offset). */
    broadcastAt: z.string().datetime({ offset: true }),
});
/**
 * HotPosts — 매분 cron 이 계산한 Hot 글 상위 3건 브로드캐스트 (Plan 03-03 / COMM-06).
 *
 * 배포 방향: apps/api HotPostsCron → Outbox `community.hot_posts.v1` → OutboxPoller →
 *             Redis Stream `widget:{streamerId}` → apps/realtime /widget.
 *
 * **DoS 억제 (T-03-03-05)**:
 *   - items 는 최대 3건.
 *   - 스트리머 설정 `hot_overlay_enabled=true` 인 경우에만 publish (cron 단계).
 *   - frequencySec throttle (cron 단계) — 본 schema 는 frequency 정보 힌트만 포함.
 *
 * `items[].score`: 직전 1시간 delta (Δview * 0.3 + Δlike * 0.7).
 */
export const widgetHotPostsSchema = z.object({
    type: z.literal('community.hot_posts.v1'),
    streamerId: z.string().uuid(),
    /** streamer_community_settings.hot_overlay_frequency_sec — 위젯이 재생 cadence 조정용. */
    frequencySec: z.number().int().min(10).max(600),
    items: z
        .array(z.object({
        postId: z.string().uuid(),
        boardId: z.string().uuid(),
        title: z.string().min(1).max(200),
        viewCount: z.number().int().nonnegative(),
        likeCount: z.number().int().nonnegative(),
        /** Δview * 0.3 + Δlike * 0.7 — 부동소수점 허용 (서버 계산). */
        score: z.number().nonnegative(),
        /** 위젯이 링크로 이동할 상대/절대 URL — 최대 500자. */
        url: z.string().min(1).max(500),
    }))
        .min(1)
        .max(3),
    /** 생성 시각 (ISO 8601 with offset). */
    generatedAt: z.string().datetime({ offset: true }),
});
/**
 * RolePromoted — 팬 등급 자동 승급 이벤트 (Plan 03-03 / COMM-03).
 *
 * 주: 본 schema 는 broadcast payload 계약 (TypeScript 소비자용) — 위젯은 현재 특별한 UI
 * 트리거 없음. alarm/notification 전용으로 Wave 2 이후 분화될 가능성. 현재는 widget 이벤트
 * 흐름과 동일 경로로 발행 (채널 widget:{streamerId}).
 */
export const widgetRolePromotedSchema = z.object({
    type: z.literal('community.role_promoted.v1'),
    streamerId: z.string().uuid(),
    fanId: z.string().uuid(),
    membershipId: z.string(),
    previousRole: z.enum(['guest', 'member', 'vip', 'streamer']),
    newRole: z.enum(['member', 'vip']),
    matchedConditions: z.array(z.string()).max(20),
    promotedAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=widget.ws.js.map